import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { If, Then, Else, When } from 'react-if';
import { nanoid } from 'nanoid';
import queryString from 'query-string';
import InfiniteLoader from 'react-virtualized/dist/es/InfiniteLoader';
import CellMeasurer from 'react-virtualized/dist/es/CellMeasurer';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import List from 'react-virtualized/dist/es/List';
import CellMeasurerCache from 'react-virtualized/dist/es/CellMeasurer/CellMeasurerCache';

import './Posts.css';
import { usePosts } from './usePosts';
import Container from 'reactstrap/es/Container';
import { Loader } from '../../components/elements/loader/Loader';
import ValidateLoader from '../../components/elements/loader/ValidateLoader';
import Post from './components/Post';
import LoadingPost from './components/LoadingPost';
import FilterQuestionBar from '../../components/post/elements/FilterQuestionBar';
import { useNotificationContext } from '../../providers/NotificationProvider';
import { useAuth } from '../../hooks/useAuth';
import { POST_PAGE_TYPES } from '../../constants/postPageTypes';
import { CREATE_POST, MY_ALL_POSTS, UPDATE_POST } from '../../constants/routes';
import { compose } from 'recompose';
import { withAuthorization } from '../../session';
import { useLocation } from 'react-router-dom';
import { checkPendingTransactions } from '../../firebase/api/users';
import { useToggle } from 'react-use';
import PostModal from './components/PostModal';
import { usePostContext } from '../../providers/PostProvider';
import WelcomeModal from './components/WelcomeModal';
import { IS_SIDEKICK_APP } from '../../constants/projects';

const cache = new CellMeasurerCache({
    defaultHeight: 550,
    fixedWidth: true,
});

const initialRefreshKey = nanoid();

const Posts = () => {
    const {
        currentUser: { uid, pendingPayments, stripeUserId },
    } = useAuth();

    const [needRefreshKey, setNewNeedRefreshKey] = useState(initialRefreshKey);
    const [activeCategory, setActiveCategory] = useState(null);
    const [isPostPreviewOpens, togglePostPreview] = useToggle(false);
    const [previewPostId, setPreviewPostId] = useState('');

    const {
        invitationsPostIds,
        unreadPostIds,
        decreasePostCount,
        viewInvitationPost,
        decreasePendingPostCount,
    } = useNotificationContext();
    const { currentPostPage, setCurrentPostPage } = usePostContext();
    const location = useLocation();
    const [initialWelcomeModalOpenState, toggleWelcomeModalState] = useToggle(false);

    useEffect(() => {
        if (stripeUserId && pendingPayments.length) {
            checkPendingTransactions(uid).then(r => r);
        }
    }, []);

    useEffect(() => {
        const parsedQuery = queryString.parse(location.search);
        if (parsedQuery.welcome) {
            toggleWelcomeModalState();
            window.history.pushState({}, document.title, window.location.pathname);
        }
    }, []);

    useEffect(() => {
        if (location.pathname === MY_ALL_POSTS) {
            setCurrentPostPage(POST_PAGE_TYPES.MY_POSTS);
        } else if (
            location.pathname.match(/^\/posts\/.+/) &&
            location.pathname !== CREATE_POST &&
            location.pathname !== UPDATE_POST
        ) {
            setPreviewPostId(location.pathname.replace('/posts/', ''));
            togglePostPreview();
        }
    }, [location, togglePostPreview]);

    const { data, isLoading, dataCount, loadMore } = usePosts(
        currentPostPage,
        needRefreshKey,
        activeCategory
    );

    const isRowLoaded = useCallback(
        ({ index }) => {
            return data.has(index);
        },
        [data]
    );

    const handleReviewsOpens = useCallback(rowIndex => {
        cache.clear(rowIndex, 0);
    }, []);

    const handleRefresh = useCallback(() => {
        setNewNeedRefreshKey(nanoid());
    }, []);

    const rowRenderer = useCallback(
        ({ index, key, style, parent }) => {
            if (!data.has(index)) {
                return (
                    <CellMeasurer
                        cache={cache}
                        key={key}
                        parent={parent}
                        columnIndex={0}
                        rowIndex={index}
                    >
                        <LoadingPost style={style} />
                    </CellMeasurer>
                );
            }

            const {
                uid,
                title,
                description,
                category,
                createdTimestamp,
                user: { firstName, lastName } = {},
                question: { type: questionType, questions } = {},
                totalDonations,
                media,
                publisherUid,
                isApprovedByAdmin,
            } = data.get(index);

            return (
                <CellMeasurer
                    cache={cache}
                    key={key}
                    parent={parent}
                    columnIndex={0}
                    rowIndex={index}
                >
                    {({ measure }) => (
                        <Post
                            measure={measure}
                            index={index}
                            style={style}
                            id={uid}
                            title={title}
                            media={media}
                            description={description}
                            categoryId={category}
                            createdTimestamp={createdTimestamp}
                            firstName={firstName}
                            lastName={lastName}
                            totalDonations={totalDonations}
                            questionType={questionType}
                            rawPost={data.get(index)}
                            questions={questions}
                            publisherUid={publisherUid}
                            isApprovedByAdmin={isApprovedByAdmin}
                            decreasePostCount={decreasePostCount}
                            decreasePendingPostCount={decreasePendingPostCount}
                            onReviewsOpen={handleReviewsOpens}
                            onRefresh={handleRefresh}
                            currentPostPage={currentPostPage}
                            approvedMode={currentPostPage === POST_PAGE_TYPES.PENDING_POSTS}
                            unreadPostIds={unreadPostIds}
                            invitationsPostIds={invitationsPostIds}
                            viewInvitationPost={viewInvitationPost}
                        />
                    )}
                </CellMeasurer>
            );
        },
        [
            data,
            handleReviewsOpens,
            currentPostPage,
            unreadPostIds,
            invitationsPostIds,
            viewInvitationPost,
        ]
    );

    const noRowsRenderer = useCallback(() => {
        return <div className="d-flex justify-content-center">No posts</div>;
    }, []);

    const handleCategoryClick = useCallback(
        category => {
            if (activeCategory === category.uid) {
                setActiveCategory(null);
            } else {
                setActiveCategory(category.uid);
            }
        },
        [activeCategory]
    );

    return (
        <>
            <Container fluid={true} className="posts__container">
                <FilterQuestionBar
                    activeCategory={activeCategory}
                    categoryToggle={handleCategoryClick}
                />
                <If condition={isLoading}>
                    <Then>
                        <ValidateLoader />
                    </Then>
                    <Else>
                        <InfiniteLoader
                            isRowLoaded={isRowLoaded}
                            loadMoreRows={loadMore}
                            rowCount={dataCount}
                            minimumBatchSize={20}
                            threshold={4}
                        >
                            {({ onRowsRendered, registerChild }) => (
                                <AutoSizer>
                                    {({ width, height }) => (
                                        <List
                                            ref={registerChild}
                                            width={width}
                                            height={height - 100}
                                            onRowsRendered={onRowsRendered}
                                            rowCount={dataCount}
                                            deferredMeasurementCache={cache}
                                            rowHeight={cache.rowHeight}
                                            rowRenderer={rowRenderer}
                                            noRowsRenderer={noRowsRenderer}
                                        />
                                    )}
                                </AutoSizer>
                            )}
                        </InfiniteLoader>
                    </Else>
                </If>
            </Container>
            <When condition={isPostPreviewOpens}>
                {() => (
                    <PostModal
                        isOpen={isPostPreviewOpens}
                        postId={previewPostId}
                        onToggle={togglePostPreview}
                    />
                )}
            </When>
            <WelcomeModal openState={initialWelcomeModalOpenState} />
        </>
    );
};

Posts.propTypes = {};

const condition = authUser => !!authUser;

export default compose(withAuthorization(condition), React.memo)(Posts);
